import React from 'react';
import numbro from 'numbro';
import { isNil, isEqual, without, find, intersection } from 'lodash';
import { checkboxesStyle } from './SubheaderCheckboxes.styles';
import { FlowStatus } from 'configuration-schemas/bindings.types';
import { Tooltip } from '@material-ui/core';

type Props = {
  flowStatus: number[];
  flowStatusOptions: FlowStatus;
  fundedStatusOptions?: FlowStatus;
  ignoreDefaultFlowStatus?: boolean;
  handleValuesUpdate: (values: number[]) => void;
  qaPrefix?: string;
  show?: boolean;
};

type State = {
  flowStatusSelection: number[];
};

function shouldIgnoreDefaults(ignoreDefaultFlowStatus?: boolean) {
  return !isNil(ignoreDefaultFlowStatus) ? ignoreDefaultFlowStatus : true;
}

export default class SubheaderCheckboxes extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      flowStatusSelection: props.flowStatus,
    };
  }

  componentDidMount() {
    const { ignoreDefaultFlowStatus, flowStatus, flowStatusOptions } = this.props;
    const ignoreDefaults = shouldIgnoreDefaults(ignoreDefaultFlowStatus);
    const initialSelection = ignoreDefaults ? flowStatus : flowStatusOptions.defaultSelection;

    this.setState({
      flowStatusSelection: initialSelection,
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (!isEqual(prevProps.flowStatus, this.props.flowStatus)) {
      this.setState({
        flowStatusSelection: this.props.flowStatus,
      });
    }

    if (!isEqual(prevState.flowStatusSelection, this.state.flowStatusSelection)) {
      this.props.handleValuesUpdate(this.state.flowStatusSelection);
    }
  }

  toggleSelection = (options: number[], selections: number[], numericValue: number) => {
    let finalSelections = selections;
    if (numericValue < 0) {
      // -1 numeric value is "all" so clear options for set
      return without(selections, ...options);
    }
    // if I don't have the value, add it, else remove it
    if (find(selections, (i) => i === numericValue) == null) {
      finalSelections = selections.concat(numericValue);
    } else {
      finalSelections = without(selections, numericValue);
    }
    let matchSel = intersection(options, finalSelections);
    // if all the options are in the selection list, clear the selections (non selected same as all selected)
    if (matchSel.length === options.length) {
      return without(finalSelections, ...options);
    } else {
      return finalSelections;
    }
  };

  // radioSelection is toggles which is active, rather than appending
  // (so, when there's two options it'll swap)
  radioSelection = (options: number[], selections: number[], numericValue: number) => {
    if (selections.indexOf(numericValue) >= 0) {
      return without(selections, numericValue);
    } else {
      return without(selections, ...options).concat(numericValue);
      // return flowStatusSelection.filter(f => !contains).concat(numericValue);
    }
  };

  handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const position = event.currentTarget.dataset.position;
    const { flowStatusOptions, fundedStatusOptions } = this.props;
    const flowValues = flowStatusOptions.values;
    const fundedValues = fundedStatusOptions?.values;
    const { flowStatusSelection } = this.state;
    if (!isNil(position)) {
      const numericValue = numbro(position).value();

      let newFlowStatus: number[] = [];

      if (numericValue > -1) {
        if (numericValue >= 10) {
          if (fundedValues == null) {
            // no support for setting funded values without funded options
            return; // do nothing
          }
          const fundedOptions = fundedValues.map((_, i) => i + 10);
          // If there's only one/two, treat as deselectable radio rather than multiselect list
          if (fundedValues.length <= 2) {
            newFlowStatus = this.radioSelection(fundedOptions, flowStatusSelection, numericValue);
            // // treat funded as radio button
          } else {
            newFlowStatus = this.toggleSelection(fundedOptions, flowStatusSelection, numericValue);
          }
        } else {
          const flowOptions = flowValues.map((_, i) => i);
          newFlowStatus = this.toggleSelection(flowOptions, flowStatusSelection, numericValue);
        }
      }

      this.setState({
        flowStatusSelection: newFlowStatus,
      });
    }
  };

  determineIsChecked(index: number, flowStatus: number[]) {
    return flowStatus.find((status) => status === index) !== undefined;
  }

  renderFundedStatus() {
    const { fundedStatusOptions, qaPrefix, show } = this.props;
    const { flowStatusSelection } = this.state;
    const empty = () => false;
    const checkboxesJsx = fundedStatusOptions ? (
      fundedStatusOptions.values.map((statusOption, index) => {
        const isChecked = this.determineIsChecked(index + 10, flowStatusSelection);
        const slug: string = statusOption.text.toLowerCase();
        return (
          <Tooltip title={statusOption.tooltip} key={slug}>
            <label
              className={`check-label${isChecked ? ' selected' : ''}`}
              data-qa={`${qaPrefix}-label-${slug}`}
              key={slug}
            >
              <input
                aria-label={statusOption.text}
                checked={isChecked}
                className="check-input"
                data-position={index + 10}
                data-type="funded-status"
                data-qa={`${qaPrefix}-checkbox-${slug}`}
                onChange={empty}
                onClick={this.handleClick}
                type="checkbox"
              />
              {statusOption.text}
            </label>
          </Tooltip>
        );
      })
    ) : (
      <div />
    );
    return (
      <fieldset className={`flow-status-checkboxes ${checkboxesStyle}`} style={{ display: 'flex' }} hidden={!show}>
        <div
          style={{
            borderLeft: '1px solid grey',
            height: 25,
            marginTop: 'auto',
            marginBottom: 'auto',
            position: 'relative',
            left: -7,
          }}
        ></div>
        <div className="flow-status-layout">{checkboxesJsx}</div>
      </fieldset>
    );
  }

  renderFlowStatusChecks = () => {
    const { qaPrefix, show } = this.props;
    const { flowStatusSelection } = this.state;
    const empty = () => false;
    const flowStatusOptions = this.props.flowStatusOptions.values;
    const selectedFlowStatus = intersection(
      flowStatusOptions.map((_, i) => i),
      flowStatusSelection,
    );
    let allJsx = <></>;

    if (flowStatusOptions && flowStatusOptions.length > 0) {
      const isChecked = selectedFlowStatus.length === 0;

      allJsx = (
        <label className={`check-label${isChecked ? ' selected' : ''}`} data-qa={`${qaPrefix}-label-all`}>
          <input
            aria-label="all"
            checked={isChecked}
            className="check-input"
            data-position="-1"
            data-qa={`${qaPrefix}-checkbox-all`}
            onChange={empty}
            onClick={this.handleClick}
            type="checkbox"
          />
          All
        </label>
      );
    }
    const checkboxesJsx = flowStatusOptions ? (
      flowStatusOptions.map((statusOption, index) => {
        const isChecked = this.determineIsChecked(index, flowStatusSelection);
        const slug: string = statusOption.text.toLowerCase();
        return (
          <Tooltip title={statusOption.tooltip} key={slug}>
            <label
              className={`check-label${isChecked ? ' selected' : ''}`}
              data-qa={`${qaPrefix}-label-${slug}`}
              key={slug}
            >
              <input
                aria-label={statusOption.text}
                checked={isChecked}
                className="check-input"
                data-position={index}
                data-qa={`${qaPrefix}-checkbox-${slug}`}
                onChange={empty}
                onClick={this.handleClick}
                type="checkbox"
              />
              {statusOption.text}
            </label>
          </Tooltip>
        );
      })
    ) : (
      <div />
    );

    return (
      <fieldset className={`flow-status-checkboxes ${checkboxesStyle}`} hidden={!show}>
        <div className="flow-status-layout">
          {allJsx}
          {checkboxesJsx}
        </div>
      </fieldset>
    );
  };

  render() {
    const hasFunded = this.props.fundedStatusOptions && this.props.fundedStatusOptions.values.length > 0;
    if (hasFunded) {
      return (
        <>
          <div style={{ overflowX: 'hidden' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {this.renderFlowStatusChecks()}
              {this.renderFundedStatus()}
            </div>
          </div>
        </>
      );
    }
    return this.renderFlowStatusChecks();
  }
}
